import { LoginContext } from '@local/login/dist/components/loginHandler/LoginContext';
import { PRIVACY_POLICY_LINK, RIGHTS_RESERVED, TERMS_OF_USE_LINK } from '@local/login/dist/strings';
import { useTrace } from '@local/web-design-system/dist/utils/trace';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/GridLegacy';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import classnames from 'classnames';
import type { PropsWithChildren } from 'react';
import { useContext } from 'react';
import { useLocation } from 'react-router';

import { LoginForm } from 'src/components/login/form';
import { LoginSideBar } from 'src/components/login/side-bar';
import { Logo } from 'src/components/logo';

import { useStyles } from './LandingPage.styles';

type LandingBackgroundProps = {
    trainingLink?: string;
};

const LandingBackground = ({
    children,
    trainingLink,
}: PropsWithChildren<LandingBackgroundProps>) => {
    const { imageList } = useContext(LoginContext);
    const { classes } = useStyles({ imageList });
    const applyTrace = useTrace('landingPage');

    return (
        <Grid container className={classes.root} automation-id="background-root">
            <Grid item md={8} lg={9} sm={12}>
                <Grid container alignItems="center">
                    <Grid item xs={12} sm={6} automation-id="background-logo-title">
                        <Grid
                            container
                            className={classnames([classes.column, classes.columnLeft])}
                        >
                            <Logo />
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Grid
                            container
                            className={classnames([
                                classes.formContainer,
                                classes.column,
                                classes.columnRight,
                            ])}
                        >
                            {children}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item justifyContent="left" alignItems="flex-start" className={classes.copy}>
                    <Typography data-dd-privacy="allow">{RIGHTS_RESERVED}</Typography>
                </Grid>
                <Grid container direction="row" className={classes.footer}>
                    <Typography variant="body2" paddingRight={1} data-dd-privacy="allow">
                        <span key={TERMS_OF_USE_LINK.linkedText}>
                            <Link
                                href={TERMS_OF_USE_LINK.href}
                                target="_blank"
                                color="inherit"
                                rel="noopener"
                                underline="always"
                                {...applyTrace('terms-of-use-link')}
                            >
                                {TERMS_OF_USE_LINK.linkedText}
                            </Link>
                        </span>
                    </Typography>
                    <Divider className={classes.divider} orientation="vertical" />
                    <Typography variant="body2" paddingLeft={1} data-dd-privacy="allow">
                        <span key={PRIVACY_POLICY_LINK.linkedText}>
                            <Link
                                href={PRIVACY_POLICY_LINK.href}
                                target="_blank"
                                color="inherit"
                                rel="noopener"
                                underline="always"
                                {...applyTrace('privacy-policy-link')}
                            >
                                {PRIVACY_POLICY_LINK.linkedText}
                            </Link>
                        </span>
                    </Typography>
                </Grid>
            </Grid>
            <Grid item md={4} lg={3} sx={{ display: { xs: 'none', md: 'block' } }}>
                <LoginSideBar trainingLink={trainingLink} />
            </Grid>
        </Grid>
    );
};

export const LandingPage = () => {
    const { state } = useLocation();

    return (
        <LandingBackground>
            <LoginForm isValidateError={state?.isValidateError} />
        </LandingBackground>
    );
};
